"use client";

import { useState } from "react";
import toast from "react-hot-toast";
import {
  Avatar,
  Button,
  Dialog,
  DialogPortal,
  DialogBackdrop,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  DialogClose,
} from "@/ui";
import DragHandle from "@components/shared/DragHandle";
import SortableItem from "@components/shared/SortableItem";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
  arrayMove,
} from "@dnd-kit/sortable";
import styles from "./ReorderRoles.module.css";

export interface ReorderItem {
  id: string | number;
  name: string;
  position?: string;
  logo?: string;
}

interface ReorderRolesProps {
  open: boolean;
  onClose: () => void;
  roles: ReorderItem[];
  onSave: (items: ReorderItem[]) => void;
  title?: string;
  description?: string;
  successMessage?: string;
}

export default function ReorderRoles({
  open,
  onClose,
  roles,
  onSave,
  title = "Re-order roles",
  description = "Drag roles up or down to reorder them — the new order will be reflected in the float.",
  successMessage = "Role order updated",
}: ReorderRolesProps) {
  const [items, setItems] = useState<ReorderItem[]>(roles ?? []);

  // Re-seed from the roles prop each time the modal opens.
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setItems(roles ?? []);
    }
  }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((role) => role.id === active.id);
    const newIndex = items.findIndex((role) => role.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    setItems(arrayMove(items, oldIndex, newIndex));
  };

  const handleSave = () => {
    onSave(items);
    onClose();
    toast.success(successMessage);
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) onClose(); }}>
      <DialogPortal>
        <DialogBackdrop />
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
            <DialogClose />
          </DialogHeader>

          <DialogBody>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={items.map((role) => role.id)} strategy={verticalListSortingStrategy}>
                <div className={styles.list}>
                  {items.map((role) => (
                    <SortableItem key={role.id} id={role.id}>
                      {({ setNodeRef, style, attributes, listeners }) => (
                        <div ref={setNodeRef} style={style} className={styles.row}>
                          <DragHandle attributes={attributes} listeners={listeners} />
                          <Avatar src={role.logo} name={role.name} alt={role.name} size="sm" />
                          <div className={styles.text}>
                            <span className={styles.name}>{role.name}</span>
                            {role.position && (
                              <span className={styles.position}>{role.position}</span>
                            )}
                          </div>
                        </div>
                      )}
                    </SortableItem>
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </DialogBody>

          <DialogFooter>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave}>
              Save order
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
